import { inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { InitializeAppConfigDataService } from '@app/service/initialize-app.config.service';
import { StatefulService } from '@app/service/stateful.service';
import { RoutingUrl } from '@shared/constant/route-const';
import { LoanType } from '@shared/enum';
import { DataLoadURL } from '@workflow/ecp-shared/model/dataload-url';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { ApiResult } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.types';
import { WorkflowPageNavigationStateService } from '@workflow/service/workflow-page-navigation-state/workflow-page-navigation-state.service';
import { Observable, from, interval, of, throwError } from 'rxjs';
import { Communication } from '../model/communication.model';
import {
  CrossSellApplyRequest,
  CrossSellOfferDetails,
  CrossSellOfferResp,
  CrossSellStatusUpdate,
  CrossSellView,
  NewCreditOffersEntity
} from '../model/cross-sell.model';

/**
 * Interface for loan product configuration
 */
export interface LoanProduct {
  groupId: string;
  groupName: string;
  productId: string;
  productName: string;
}
import { BundleHelperService } from './bundle-helper.service';
import { OlaOnClickFunctionsService } from '@workflow/ecp-workflow/ola/services/logic-functions/on-click-functions.service';
import { catchError, delay, filter, map, switchMap, take, timeout } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CrossSellService {
  private readonly hostName = 'consumerPortalApi';
  private readonly apiService = inject(ApiService);
  private readonly statefulService = inject(StatefulService);
  private readonly applicationApiService = inject(ApplicationApiService);
  private readonly userService = inject(UserService);
  private readonly pageNavigationStateService = inject(WorkflowPageNavigationStateService);
  private readonly router = inject(Router);
  private readonly bundleHelperService = inject(BundleHelperService);
  private readonly initializeAppConfigDataService = inject(InitializeAppConfigDataService);
  // move specific logic any common service;
  private readonly olOnClickFunctionsService = inject(OlaOnClickFunctionsService);


  // Stores user custom variables enclosed in [%..%] from given text
  public targetVariables = [];

  // Stored matched custom variable from given string
  public customVariables: string[] = [];

  public GetCrossSellConfiguration(applicationid: string): Observable<ApiResult<CrossSellView[]>> {
    const urlObj = {
      hostName: this.hostName,
      method: 'GET',
      url: `consumer/crosssell/${applicationid}`
    } as DataLoadURL;
    return this.apiService.request<ApiResult<CrossSellView[]>>({ urlObj });
  }

  public SubmitCrossSell(objCrossSellProduct: CrossSellOfferDetails): Observable<ApiResult<CrossSellOfferDetails>> {
    const urlObj = {
      hostName: this.hostName,
      method: 'POST',
      url: 'member/oao/integration/approvecrosssell'
    } as DataLoadURL;

    return this.apiService.request<ApiResult<CrossSellOfferDetails>>({ urlObj, body: objCrossSellProduct });
  }

  public startCrossSellApplication(objCrossSellApply: CrossSellApplyRequest): Observable<ApiResult<CrossSellOfferResp>> {
    const urlObj = {
      hostName: this.hostName,
      method: 'POST',
      url: 'consumer/crosssell/apply'
    } as DataLoadURL;

    return this.apiService.request<ApiResult<CrossSellOfferResp>>({ urlObj, body: objCrossSellApply });
  }

  public SaveCommunicationMessage(communicationObj: Communication): Observable<ApiResult<Communication>> {
    const urlObj = {
      hostName: this.hostName,
      method: 'POST',
      url: 'integration/l360/Communication/Save'
    } as DataLoadURL;

    return this.apiService.request<ApiResult<Communication>>({ urlObj, body: communicationObj });
  }

  public UpdateCrossSellInterestedStatus(
    objCrossSellProduct: CrossSellStatusUpdate,
    callback: (res: ApiResult<CrossSellOfferDetails>) => void,
    errorCallback: (err) => void
  ): void {
    const urlObj = {
      hostName: this.hostName,
      method: 'POST',
      url: 'member/oao/integration/crossSellUpdateStatus/Interested'
    } as DataLoadURL;

    this.apiService.requestApi<ApiResult<CrossSellStatusUpdate>>({
      urlObj,
      body: objCrossSellProduct,
      callback,
      errorCallback
    });
  }
  public stipulationListToDisplayHelper = (stipulations: string[]): string[] => {
    stipulations.forEach((stipulation, i) => {
      stipulations[i] = stipulation.length > 50 ? stipulation.slice(0, 50) + '...' : stipulation;
    });
    stipulations = stipulations.length > 4 ? stipulations.slice(0, 4) : stipulations;
    return stipulations;
  };

  public stipulationHelper = (stipulations: string): string[] => {
    if (stipulations) {
      return stipulations.split('|').map((item) => item.trim());
    }
    return [];
  };

  public stringReplaceCustomVar(crossSellOffer): NewCreditOffersEntity {
    if (!crossSellOffer) return;
    if (crossSellOffer.headline) crossSellOffer.headline = this.parseCustomVariable(crossSellOffer?.headline, crossSellOffer);
    if (crossSellOffer.subtitle) crossSellOffer.subtitle = this.parseCustomVariable(crossSellOffer?.subtitle, crossSellOffer);
    return crossSellOffer;
  }

  private setCustomVariableData(targetDataObject) {
    this.targetVariables = [
      { name: 'offer.loan.amount', value: targetDataObject?.decisionAmount },
      { name: 'offer.rate', value: targetDataObject?.decisionRate },
      { name: 'offer.term', value: targetDataObject?.decisionTerm },
      { name: 'offer.monthly.payment', value: targetDataObject?.estimatedPayment },
      { name: 'offer.monthly.savings', value: targetDataObject?.estimatedMonthlySavings }
    ];
  }

  private createMatchedArray(temp: string): string {
    const re = '\\[.*?\\]';
    const customVariableRegEx = new RegExp(`${re}`, 'g');
    const matchedString = temp.match(customVariableRegEx) ? temp.match(customVariableRegEx)?.toString() : '';
    if (matchedString) return matchedString;
    else return '';
  }

  public parseCustomVariable(inputString: string, targetDataObject): string {
    this.setCustomVariableData(targetDataObject);
    let tempStr = inputString;
    this.customVariables = this.createMatchedArray(inputString) ? this.createMatchedArray(inputString).split(',') : [];
    if (this.customVariables && this.customVariables.length > 0) {
      for (const customVariable of this.customVariables) {
        const str = customVariable.substring(customVariable.indexOf('[%') + 2, customVariable.indexOf('%]'));
        const replacingValue = this.targetVariables.find((x) => x.name.toLocaleLowerCase() === str.toLocaleLowerCase());
        if (replacingValue) {
          tempStr = this.replaceCustomVariables(tempStr, customVariable, replacingValue?.value);
        } else if (str === '[%') {
          tempStr = this.replaceCustomVariables(tempStr, customVariable, customVariable);
        } else {
          tempStr = this.replaceCustomVariables(tempStr, customVariable, '(ERR)');
        }
      }
    }
    return tempStr;
  }

  private replaceCustomVariables(str: string, find: string, replace: string): string {
    const replaceString = str;
    const newString = replaceString.replace(find, replace);
    return newString;
  }

  public displayStipulationOnSlideInPanel = (stipulationList: string[]): boolean => {
    if (stipulationList.length > 4) {
      return true;
    }
    return stipulationList.some((stipulation) => stipulation.length > 50);
  };

  /**
   * Navigates to a deep link offer using the provided token and product identifiers
   * @param token - Authentication token for the user
   * @param groupId - The loan group identifier
   * @param productId - The specific product identifier
   * @returns An Observable that emits true when successful or errors when failed
   */
  public navigateToDeepLinkOffer(token: string, groupId: string, productId: string): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      // Find the product configuration based on groupId and productId
      const product = this.findProductConfiguration(groupId, productId);

      if (!product) {
        subscriber.error(new Error('Unable to find product configuration'));
        return;
      }

      // Setup application context
      this.setupApplicationContext(token);

      // Process the OLA bundle update
      this.processOlaBundle()
        .subscribe({
          next: (success) => {
            if (success) {
              this.router.navigate([RoutingUrl.OLA.LoanType], { loantype: product.groupName, loantypeoption: product.productName } as Params);
              subscriber.next(true);
              subscriber.complete();
            } else {
              subscriber.error(new Error('Application submission failed'));
            }
          },
          error: (err) => {
            // Error is propagated to subscriber
            subscriber.error(err);
          }
        });
    });
  }

  /**
   * Confirms and processes a one-click loan offer using the provided token and product identifiers
   * @param token - Authentication token for the user
   * @param groupId - The loan group identifier
   * @param productId - The specific product identifier
   * @returns An Observable that emits true when successful or errors when failed
   */
  public confirmOneClickOffer(token: string, groupId: string, productId: string): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      // Find the product configuration based on groupId and productId
      const product = this.findProductConfiguration(groupId, productId);

      if (!product) {
        subscriber.error(new Error('Unable to find product configuration'));
        return;
      }

      // Setup application context
      this.setupApplicationContext(token);

      // Process the OLA bundle update
      this.processOlaBundle()
        .pipe(
          // After bundle update, get the application ID
          switchMap(() => this.getApplicationId()),
          // Once we have an application ID, submit the application
          switchMap(appId => {
            if (!appId) {
              throw new Error('Failed to generate application ID');
            }

            this.applicationApiService.OLAAppSubmitting = false;
            return this.submitApplication();
          })
        )
        .subscribe({
          next: (success) => {
            if (success) {
              this.olOnClickFunctionsService.handleAfterSubmission();
              subscriber.next(true);
              subscriber.complete();
            } else {
              subscriber.error(new Error('Application submission failed'));
            }
          },
          error: (err) => {
            // Error is propagated to subscriber
            subscriber.error(err);
          }
        });
    });
  }

  /**
   * Finds a product configuration by group and product IDs
   */
  private findProductConfiguration(groupId: string, productId: string): LoanProduct | undefined {
    return this.initializeAppConfigDataService.appConfigData.admin.olaConfiguration.consumerLoanGroups
      .filter(group => group.groupId === groupId)
      .flatMap(group =>
        group.loanProducts.map(product => ({
          groupId: group.groupId,
          groupName: group.displayName,
          productId: product.productId,
          productName: product.productName
        }))
      )
      .find(product => product.groupId === groupId && product.productId === productId);
  }

  /**
   * Sets up the application context with authentication and user state
   */
  private setupApplicationContext(token: string): void {
    this.resetAppId();
    this.statefulService.wrap('ss:auth-ola').value = token;
    this.userService.user.token = token;
    this.userService.flowType = 'OLA';
    this.userService.isConsumer = true;
    this.statefulService.wrap('mem:primary-application-status').value = true;
    this.statefulService.wrap('ola:olaApplicationInformation/isEligibilityValidated').value = true;
  }

  /**
   * Function to reset the application ID
   * This is used when the user applies for membership from the task list
   * to prevent the appID of the loan from being used in the next session.
   */
  private resetAppId(): void {
    const resetAppNumber = -1;
    const id = JSON.stringify(resetAppNumber.toString());
    sessionStorage.setItem('appNumber', id);
    this.pageNavigationStateService.setStoragePrefix('ola');
    this.pageNavigationStateService.retrievePageState();
  }

  /**
   * Process OLA bundle with state updates and returns an Observable
   */
  private processOlaBundle(): Observable<boolean> {
    return from(this.bundleHelperService.refreshBundleAsync()).pipe(
      delay(500),
      map(() => {
        this.statefulService.wrap('ola:borrowerInfo/autoFillOption').value = 'DXPrefill';
        this.statefulService.wrap('ola:borrowerInfo/isPrefilled').value = true;
        this.statefulService.wrap('ola:borrowerInfo/autoFillStatus').value = true;
        this.statefulService.wrap('ss:ola-first-time-navigation').value = true;
        this.statefulService.wrap('ss:fresh-app-from-postapp').value = true;
        this.statefulService.wrap('ss:current-patch-request-body').value = null;

        // Setup application generation
        this.applicationApiService.generateAppId = true;
        this.applicationApiService.OLAAppSubmitting = true;
        this.applicationApiService.partialApplication = true;

        return true;
      }),
      delay(1000),
      switchMap(() => this.updateBundleToDXAPI())
    );
  }

  /**
   * Updates bundle data to DX API system
   */
  private updateBundleToDXAPI(): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      this.applicationApiService.updateBundleDataToDDMS(
        LoanType.OLA.toLowerCase(),
        (res: any) => { // Use any type here to handle the varied response types
          this.bundleHelperService.refreshBundleAsync()
            .then(() => {
              // Handle different response types
              let isSuccess = false;
              if (typeof res === 'boolean') {
                isSuccess = res;
              } else if (typeof res === 'number') {
                isSuccess = res === 200;
              } else if (res && typeof res === 'object' && 'isSuccess' in res) {
                isSuccess = !!res.isSuccess;
              }
              subscriber.next(isSuccess);
              subscriber.complete();
            })
            .catch(err => subscriber.error(err));
        },
        (err) => {
          subscriber.error(err);
        },
        true
      );
    });
  }

  /**
   * Gets the application ID with retry logic
   * If ID doesn't exist, refreshes the bundle to trigger ID generation
   */
  private getApplicationId(): Observable<string> {
    return interval(500).pipe(
      switchMap(() => {
        const appId = this.statefulService.wrap('ola:borrowerInfo/applicationID').valueString;

        // If we have an application ID, return it
        if (appId) {
          return of(appId);
        }

        // If no application ID exists, refresh the bundle and return null
        // This will cause the filter to skip this value
        return from(this.bundleHelperService.refreshBundleAsync()).pipe(
          map(() => null)
        );
      }),
      filter(appId => !!appId),
      take(1),
      timeout(15000), // Increased timeout to allow for bundle refresh
      catchError(() => throwError(() => new Error('Timeout waiting for application ID after bundle refresh')))
    );
  }

  /**
   * Submit the OLA application
   */
  private submitApplication(): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      this.applicationApiService.submitOLAApplication(
        (success) => {
          subscriber.next(!!success);
          subscriber.complete();
        },
        (error) => {
          subscriber.error(error);
        }
      );
    });
  }
}
